import { invalidateEnvironmentLists } from '@/services/db/environment/cache';
import { useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCurrentAccountUncached, seedCurrentAccountCache, type AccountResponse } from './account-api';
import { getEnvironment } from '@/services/db/environment/queries';
// Source module import (not the barrel) to match the signup page's pre-auth import graph.
import { getApiClientErrorStatus } from '@/services/api/errors';
import { deleteCrossAppCacheKeys } from '@/utils/cross-app-cache';
import { POLLING_DELAY_MS } from './account-readiness-timing';

/**
 * Readiness itself retries without limit; only consecutive transport
 * failures count toward giving up.
 */
export const MAX_TRANSPORT_FAILURES = 3;

export const ACCOUNT_READINESS_QUERY_KEY = ['signup', 'post-registration-readiness'] as const;

/** The account exists but its workspace isn't usable yet — retry, don't give up. */
class AccountNotReadyError extends Error {}

/**
 * The new user's default workspace: the earliest-created accessible
 * environment. A missing one means "not ready yet", not an error.
 */
export function resolveDefaultEnvironmentId(response: AccountResponse): string | null {
  const accessibleIds = new Set(response.user?.accessible_environment_ids ?? []);
  const candidates = (response.environments ?? []).filter(
    (environment) => accessibleIds.has(environment.id) && environment.created_at != null,
  );
  candidates.sort((a, b) => a.created_at - b.created_at);
  return candidates[0]?.id ?? null;
}

/** An `AccountResponse` whose account slot is proven present. */
type ReadyAccountResponse = AccountResponse & { account: NonNullable<AccountResponse['account']> };

async function checkAccountReady(signal: AbortSignal): Promise<ReadyAccountResponse> {
  const response = await fetchCurrentAccountUncached(signal);
  const { account } = response;
  if (account == null) {
    // A payload without its account slot is the not-yet-provisioned shape —
    // the same "keep polling" signal as a missing environment.
    throw new AccountNotReadyError('new account payload is not available yet');
  }
  const environmentId = resolveDefaultEnvironmentId(response);
  if (environmentId == null) {
    throw new AccountNotReadyError('new account has no accessible environment yet');
  }
  try {
    await getEnvironment(Number(environmentId), signal);
  } catch (error) {
    // The account payload can list the workspace before it is queryable — a 404
    // here is the same "not ready yet" signal as a missing id, not a failure.
    if (getApiClientErrorStatus(error) === 404) {
      throw new AccountNotReadyError('new workspace is not queryable yet');
    }
    throw error;
  }
  return { ...response, account };
}

export type AccountReadiness = {
  /** The payload that proved readiness; `undefined` while polling or after giving up. */
  readyResponse: AccountResponse | undefined;
  /** The new default workspace's id, once ready. */
  defaultEnvironmentId: string | null;
  /** The transport-failure budget ran out; readiness was never observed. */
  isError: boolean;
};

/**
 * Polls until the new account's workspace exists and loads, then seeds the
 * cross-app account cache and invalidates auth/workspace-list queries.
 */
export function useAccountReadiness({ enabled = true }: { enabled?: boolean } = {}): AccountReadiness {
  // `failureCount` counts every failure, readiness ones included, so the
  // transport budget is tracked on its own.
  const transportFailuresRef = useRef(0);
  const queryClient = useQueryClient();

  const { data: readyResponse, isError } = useQuery({
    queryKey: ACCOUNT_READINESS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await checkAccountReady(signal);
      deleteCrossAppCacheKeys('/accounts/current', `/accounts/${response.account.id}`);
      seedCurrentAccountCache(response);
      void queryClient.invalidateQueries({ queryKey: ['authenticated'] });
      void invalidateEnvironmentLists(queryClient);
      return response;
    },
    // Unmount aborts the in-flight attempt via the query signal.
    retry: (_failureCount, error) => {
      if (error instanceof AccountNotReadyError) {
        // The request itself succeeded, so the failure budget starts over.
        transportFailuresRef.current = 0;
        return true;
      }
      transportFailuresRef.current += 1;
      return transportFailuresRef.current <= MAX_TRANSPORT_FAILURES;
    },
    retryDelay: POLLING_DELAY_MS,
    staleTime: 0,
    gcTime: 0,
    enabled,
  });

  return {
    readyResponse,
    defaultEnvironmentId: readyResponse == null ? null : resolveDefaultEnvironmentId(readyResponse),
    isError,
  };
}
